import { useEffect, useMemo, useRef, useState } from "react";
import {
  AutocompleteInput,
  BooleanInput,
  Loading,
  TextInput,
  regex,
  required,
  useGetList,
  useGetOne,
  useRecordContext,
  type AutocompleteInputProps,
  type RaRecord,
} from "react-admin";
import { Box, Typography } from "@mui/material";
import { useFormContext, useWatch } from "react-hook-form";

import RelayItsSourceChip, { type RelayItsSource } from "./RelayItsSourceChip";
import {
  formatRelayAccessAddressValue,
  resolveItsPreloadContactMobile,
  resolveRelayItsSourceFromLookups,
  type RelayAccessItsdata,
} from "./utils";

export const ITS_ID_PATTERN = /^\d{8}$/;
const ITS_LOOKUP_MIN_CHARS = 3;

type GatedRelayEvent = RaRecord & {
  name?: string;
  requiresAccessGrant?: boolean;
};

const gatedEventOptionText = (record: GatedRelayEvent) => record.name ?? "—";

const sortGatedEventsByNameDesc = (events: GatedRelayEvent[]) =>
  [...events].sort((a, b) => String(b.name ?? "").localeCompare(String(a.name ?? "")));

const itsOptionText = (record: { Full_Name?: string | null; ITS_ID?: string }) =>
  `${record.Full_Name ?? ""} (${record.ITS_ID ?? ""})`.trim();

type ItsdataChoice = {
  id: string;
  ITS_ID: string;
  Full_Name: string;
};

export const formatItsAddressFromRecord = (record: RaRecord): string => {
  const parts = [
    record.Address,
    record.Building,
    record.Street,
    record.Area,
    record.City,
    record.State,
    record.Pincode,
  ]
    .map((part) => (typeof part === "string" ? part.trim() : ""))
    .filter(Boolean);
  return parts.join(", ");
};

const formatSabilAddress = (record: RaRecord): string => {
  const parts = [record.address, record.mohallah]
    .map((part) => (typeof part === "string" ? part.trim() : ""))
    .filter(Boolean);
  return parts.join(", ");
};

function RelayItsAutocomplete(props: Pick<AutocompleteInputProps, "validate" | "readOnly">) {
  const [searchText, setSearchText] = useState("");
  const itsId = useWatch({ name: "itsId" });
  const trimmedSearch = searchText.trim();
  const canSearch = trimmedSearch.length >= ITS_LOOKUP_MIN_CHARS;

  const { data: directoryRows = [], isPending } = useGetList(
    "itsdata",
    {
      filter: { q: trimmedSearch },
      pagination: { page: 1, perPage: 25 },
    },
    { enabled: canSearch && !props.readOnly }
  );

  const choices = useMemo((): ItsdataChoice[] => {
    const list: ItsdataChoice[] = (directoryRows || []).map((row) => ({
      id: String(row.ITS_ID ?? row.id),
      ITS_ID: String(row.ITS_ID ?? "").trim(),
      Full_Name: String(row.Full_Name ?? "").trim(),
    }));
    const selected = typeof itsId === "string" ? itsId.trim() : "";
    if (selected && !list.some((choice) => choice.ITS_ID === selected)) {
      list.unshift({ id: selected, ITS_ID: selected, Full_Name: "" });
    }
    return list;
  }, [directoryRows, itsId]);

  return (
    <AutocompleteInput
      source="itsId"
      label="ITS ID"
      choices={choices}
      optionText={itsOptionText}
      optionValue="ITS_ID"
      loading={canSearch && isPending}
      onInputChange={(_, value) => setSearchText(value ?? "")}
      shouldRenderSuggestions={(val: string) => val.trim().length >= ITS_LOOKUP_MIN_CHARS}
      noOptionsText={
        trimmedSearch.length > 0 && trimmedSearch.length < ITS_LOOKUP_MIN_CHARS
          ? "Type at least 3 characters to search"
          : "No matching ITS records"
      }
      validate={props.validate}
      readOnly={props.readOnly}
      debounce={300}
      fullWidth
      helperText={
        props.readOnly
          ? "ITS ID cannot be changed after access is granted"
          : "Search by ITS ID or name (minimum 3 characters)"
      }
    />
  );
}

function RelayItsSourceIndicator() {
  const record = useRecordContext<RaRecord>();
  const itsId = useWatch({ name: "itsId" });
  const normalizedItsId = typeof itsId === "string" ? itsId.trim() : "";
  const shouldFetch = ITS_ID_PATTERN.test(normalizedItsId);

  const {
    data: fetchedIts,
    isPending: itsPending,
    error: itsError,
  } = useGetOne("itsdata", { id: normalizedItsId }, { enabled: shouldFetch, retry: false });

  const { data: sabilRows = [], isPending: sabilPending } = useGetList(
    "sabilData",
    {
      filter: { q: normalizedItsId },
      pagination: { page: 1, perPage: 5 },
    },
    { enabled: shouldFetch }
  );

  if (!shouldFetch) {
    return null;
  }

  if (itsPending || sabilPending) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Checking directory and sabil records…
      </Typography>
    );
  }

  const embeddedIts = record?.itsdata as RelayAccessItsdata | undefined;
  const embeddedMatches =
    Boolean(embeddedIts) &&
    String(record?.itsId ?? embeddedIts?.ITS_ID ?? "").trim() === normalizedItsId;
  const hasDirectory = embeddedMatches ? Boolean(embeddedIts) : Boolean(fetchedIts && !itsError);
  const hasSabil = sabilRows.some((row) => String(row.itsNo ?? "").trim() === normalizedItsId);
  const source: RelayItsSource = resolveRelayItsSourceFromLookups(hasDirectory, hasSabil);

  return (
    <Box sx={{ mb: 2 }}>
      <RelayItsSourceChip source={source} />
    </Box>
  );
}

function RelayContactMobileField({ copyFromIts }: { copyFromIts: boolean }) {
  const record = useRecordContext<RaRecord>();
  const itsId = useWatch({ name: "itsId" });
  const contactMobile = useWatch({ name: "contactMobile" });
  const { setValue } = useFormContext();
  const normalizedItsId = typeof itsId === "string" ? itsId.trim() : "";
  const shouldFetch = copyFromIts && ITS_ID_PATTERN.test(normalizedItsId);
  const lastCopiedItsIdRef = useRef<string | null>(null);
  const seededSnapshotRef = useRef(false);

  const {
    data: itsRecord,
    isPending,
    error: itsError,
  } = useGetOne("itsdata", { id: normalizedItsId }, { enabled: shouldFetch, retry: false });

  useEffect(() => {
    if (copyFromIts || seededSnapshotRef.current) {
      return;
    }

    const snapshot =
      typeof record?.contactMobileSnapshot === "string" ? record.contactMobileSnapshot.trim() : "";
    if (!snapshot || contactMobile) {
      seededSnapshotRef.current = true;
      return;
    }

    setValue("contactMobile", snapshot, { shouldDirty: false });
    seededSnapshotRef.current = true;
  }, [copyFromIts, contactMobile, record?.contactMobileSnapshot, setValue]);

  useEffect(() => {
    if (!copyFromIts || !shouldFetch) {
      if (lastCopiedItsIdRef.current !== null) {
        setValue("contactMobile", "", { shouldDirty: false });
        lastCopiedItsIdRef.current = null;
      }
      return;
    }

    if (isPending) {
      return;
    }

    if (lastCopiedItsIdRef.current === normalizedItsId) {
      return;
    }

    const nextMobile =
      itsRecord && !itsError ? resolveItsPreloadContactMobile(itsRecord as RelayAccessItsdata) : "";

    if (!nextMobile) {
      return;
    }

    setValue("contactMobile", nextMobile, { shouldDirty: true });
    lastCopiedItsIdRef.current = normalizedItsId;
  }, [copyFromIts, shouldFetch, itsRecord, itsError, isPending, normalizedItsId, setValue]);

  const loadingMobile = shouldFetch && isPending;

  return (
    <TextInput
      source="contactMobile"
      label="WhatsApp / mobile"
      fullWidth
      disabled={loadingMobile}
      parse={(value) => (typeof value === "string" ? value.trim() : "")}
      helperText={
        loadingMobile
          ? "Loading WhatsApp or mobile from directory…"
          : copyFromIts
            ? "Pre-filled from directory WhatsApp, then mobile; edit before saving if needed"
            : "Contact number stored with this access request"
      }
    />
  );
}

function RelayAddressField({
  addressSource,
  copyFromIts,
}: {
  addressSource: "address" | "addressSnapshot";
  copyFromIts: boolean;
}) {
  const itsId = useWatch({ name: "itsId" });
  const { setValue } = useFormContext();
  const normalizedItsId = typeof itsId === "string" ? itsId.trim() : "";
  const shouldFetch = copyFromIts && ITS_ID_PATTERN.test(normalizedItsId);
  const lastCopiedItsIdRef = useRef<string | null>(null);

  const {
    data: itsRecord,
    isPending: itsPending,
    error: itsError,
  } = useGetOne("itsdata", { id: normalizedItsId }, { enabled: shouldFetch, retry: false });

  const { data: sabilRows = [], isPending: sabilPending } = useGetList(
    "sabilData",
    {
      filter: { q: normalizedItsId },
      pagination: { page: 1, perPage: 5 },
    },
    { enabled: shouldFetch }
  );

  const sabilRecord = sabilRows.find((row) => String(row.itsNo ?? "").trim() === normalizedItsId);

  useEffect(() => {
    if (!copyFromIts || !shouldFetch) {
      if (lastCopiedItsIdRef.current !== null) {
        setValue(addressSource, "", { shouldDirty: false });
        lastCopiedItsIdRef.current = null;
      }
      return;
    }

    if (itsPending || sabilPending) {
      return;
    }

    if (lastCopiedItsIdRef.current === normalizedItsId) {
      return;
    }

    const directoryAddress = itsRecord && !itsError ? formatItsAddressFromRecord(itsRecord) : "";
    const sabilAddress = sabilRecord ? formatSabilAddress(sabilRecord) : "";
    const nextAddress = directoryAddress || sabilAddress;

    if (!nextAddress) {
      return;
    }

    setValue(addressSource, nextAddress, { shouldDirty: true });
    lastCopiedItsIdRef.current = normalizedItsId;
  }, [
    addressSource,
    copyFromIts,
    shouldFetch,
    itsRecord,
    itsError,
    itsPending,
    sabilPending,
    sabilRecord,
    normalizedItsId,
    setValue,
  ]);

  const loadingAddress = shouldFetch && (itsPending || sabilPending);

  return (
    <TextInput
      source={addressSource}
      label="Address"
      multiline
      minRows={3}
      fullWidth
      validate={required()}
      disabled={loadingAddress}
      format={addressSource === "addressSnapshot" ? formatRelayAccessAddressValue : undefined}
      parse={(value) => (typeof value === "string" ? value : "")}
      helperText={
        loadingAddress
          ? "Loading address from directory or sabil…"
          : copyFromIts
            ? "Copied from directory or sabil when available; edit before saving if needed"
            : "Delivery address captured with this request"
      }
    />
  );
}

function GatedEventField({ readOnly }: { readOnly: boolean }) {
  const { data = [], isPending } = useGetList<GatedRelayEvent>("relay-events", {
    pagination: { page: 1, perPage: 100 },
    filter: { requiresAccessGrant: true },
  });

  const gatedEvents = useMemo(
    () => sortGatedEventsByNameDesc(data.filter((event) => event.requiresAccessGrant)),
    [data]
  );

  if (isPending) {
    return <Loading />;
  }

  return (
    <AutocompleteInput
      source="relayEventId"
      label="Relay event"
      choices={gatedEvents}
      optionText={gatedEventOptionText}
      validate={required()}
      readOnly={readOnly}
      fullWidth
      helperText={
        readOnly
          ? "Relay event cannot be changed after access is granted"
          : gatedEvents.length === 0
            ? "No access-gated relay events configured yet"
            : undefined
      }
    />
  );
}

type RelayAccessRequestFormFieldsProps = {
  mode: "create" | "edit";
};

export default function RelayAccessRequestFormFields({ mode }: RelayAccessRequestFormFieldsProps) {
  const identityReadOnly = mode === "edit";
  const addressSource = mode === "create" ? "address" : "addressSnapshot";

  return (
    <>
      <GatedEventField readOnly={identityReadOnly} />

      <RelayItsAutocomplete
        readOnly={identityReadOnly}
        validate={[required(), regex(ITS_ID_PATTERN, "ITS ID must be exactly 8 digits")]}
      />

      <RelayItsSourceIndicator />

      <RelayContactMobileField copyFromIts={!identityReadOnly} />

      <RelayAddressField addressSource={addressSource} copyFromIts={!identityReadOnly} />

      <BooleanInput
        source="audioOnly"
        label="Audio only"
        helperText="When enabled, the relay app plays audio from the YouTube stream without video"
      />

      <TextInput
        source="reason"
        label="Reason / notes"
        multiline
        fullWidth
        helperText="Optional note recorded with the request"
      />

      <TextInput
        source="remarks"
        label="Remarks"
        multiline
        fullWidth
        helperText="Optional internal note for staff (not shown in the relay app)"
      />
    </>
  );
}

export function transformRelayAccessRequestCreate(data: RaRecord) {
  const rawAddress = typeof data.address === "string" ? data.address : undefined;
  const rawContactMobile =
    typeof data.contactMobile === "string" ? data.contactMobile.trim() : undefined;

  return {
    relayEventId: data.relayEventId,
    itsId: data.itsId,
    address: rawAddress ? formatRelayAccessAddressValue(rawAddress) : undefined,
    reason: data.reason,
    remarks: data.remarks,
    audioOnly: data.audioOnly,
    contactMobile: rawContactMobile || undefined,
  };
}

export function transformRelayAccessRequestUpdate(data: RaRecord) {
  const rawAddress =
    typeof data.addressSnapshot === "string"
      ? data.addressSnapshot
      : typeof data.address === "string"
        ? data.address
        : undefined;
  const rawContactMobile =
    typeof data.contactMobile === "string" ? data.contactMobile.trim() : undefined;

  return {
    reason: data.reason,
    remarks: data.remarks,
    address: rawAddress ? formatRelayAccessAddressValue(rawAddress) : undefined,
    audioOnly: data.audioOnly,
    relayEventId: data.relayEventId,
    itsId: data.itsId,
    contactMobile: rawContactMobile || undefined,
  };
}
